import idx from 'idx'
import { inject, observer } from 'mobx-react'
import * as React from 'react'
import Helmet from 'react-helmet'
import { PlayerState } from '../models/player'
import { IRootStore } from '../models/root'
import { IScript, ScriptState } from '../models/script'
import CardStack from './CardStack'
import CircularProgressBar from './CircularProgressBar'
import Header from './Header'
import { IconName } from './Icon'
import MailingListModal from './MailingListModal'
import ModuleIntro from './ModuleIntro'
import Player from './Player'
import PrimaryButton from './PrimaryButton'

import './Module.scss'
import SecondaryButton from './SecondaryButton'

export interface IFileNode {
  id: string
  name: string
  publicURL: string
}

export interface IModuleDataResponse {
  allScriptsYaml: {
    edges: Array<{ node: IScript }>
  }
  allFile: {
    edges: [
      {
        node: IFileNode
      }
    ]
  }
}

interface IModuleProps {
  data: IModuleDataResponse
}

interface InjectedProps extends IModuleProps {
  store: IRootStore
}

@inject('store')
@observer
class Module extends React.Component<IModuleProps> {
  get injected() {
    return this.props as InjectedProps
  }

  public images: Map<string, string> = new Map<string, string>()

  public componentDidMount() {
    const { data } = this.props
    const { store } = this.injected

    // Store all images (.svg)
    data.allFile.edges.map(edge => {
      const publicURL = idx(edge, _ => _.node.publicURL)

      if (!publicURL) {
        return
      }

      const ext = publicURL.substr(publicURL.lastIndexOf('.') + 1)

      if (ext === 'svg') {
        this.images.set(edge.node.name, edge.node.publicURL)
      }
    })

    const script: IScript = data.allScriptsYaml.edges[0].node

    if (!script) {
      console.error('Unable to find script for module')
    }

    store.loadModule(script)
    store.player.loadRandomPlayerColors()
  }

  public componentWillUnmount() {
    const { store } = this.injected

    // Clear all module data
    store.resetModule()
  }

  public render() {
    const { store } = this.injected
    const { data } = this.props

    return (
      <div className="module">
        <div className="page__backdrop__gradient" />
        {store.player.state === PlayerState.inactive
          ? this.renderIntro()
          : null}
        {store.player.state === PlayerState.loading
          ? this.renderPlayerLoading()
          : null}
        {store.player.state !== PlayerState.inactive &&
        store.player.state !== PlayerState.completed ? (
          <Player data={data} />
        ) : null}
        {store.player.state === PlayerState.loaded ||
        store.player.state === PlayerState.playing ||
        store.player.state === PlayerState.paused ||
        store.player.state === PlayerState.ended
          ? this.renderPlaying()
          : null}
        {store.player.state === PlayerState.completed
          ? this.renderCompleted()
          : null}
      </div>
    )
  }

  private renderIntro() {
    const {data} = this.props;

    return (
      <div className="page">
        <Header bwlogo={true} />
        <ModuleIntro script={data.allScriptsYaml.edges[0].node} />
      </div>
    )
  }

  private renderPlayerLoading() {
    const { store } = this.injected
    const { data } = this.props

    return (
      <div className="loadingscreen">
        <CircularProgressBar
          fillColor={'#000'}
          bgColor={'rgba(0, 0, 0, 0.05)'}
          percentage={store.player.progressPercentage}
          diameter={40}
          strokeWidth={4}
        />
        <h3>Loading audio</h3>
        <SecondaryButton
          className="bottomlink"
          iconName={IconName.x}
          text="Cancel"
          callback={this.handleEndClick}
        />
      </div>
    )
  }
  private renderPlaying() {
    const { store } = this.injected
    return (
      <div className="playingscreen">
        <CardStack images={this.images} />
      </div>
    )
  }

  private renderCompleted() {
    const { store } = this.injected

    return (
      <div className="page">
        <Header bwlogo={true} />
        <div className="endedscreen">
          <div
            className={`module__icon--${store.script.data &&
              store.script.data.shortname} playing__icon`}
          />
          <div className="endedscreen__top">
            <h1 className="small_title">
              We hope you had a great conversation.
            </h1>
            <p className="normal_paragraph">
              Thank you for trying <em>Supportive</em>.
            </p>
            <PrimaryButton className="endlink" text="Return Home" to="/" />
          </div>
          <SecondaryButton
            className="endedreplay"
            iconName={IconName.replay}
            text="Restart"
            callback={this.handleReplayClick}
          />
        </div>
        <MailingListModal type="white" />
      </div>
    )
  }

  private handleEndClick = () => {
    const { store } = this.injected
    store.player.reset()
    store.player.setPlayerState(PlayerState.inactive)
  }

  private handleReplayClick = () => {
    const { store } = this.injected

    store.player.reset()
    store.player.setPlayerState(PlayerState.inactive)
  }
}

export default Module



// WEBPACK FOOTER //
// ./src/components/Module.tsx